import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Select,
  MenuItem,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import usePlanStore from '../store/planStore';
import { apiClient } from '../services/api';
import { useSnackbar } from '../contexts/SnackbarContext';
import ReceiptUpload from '../components/receipts/ReceiptUpload';
import GroceryListContainer from '../components/shopping/GroceryListContainer';
import { processGroceryData, flattenGroceryData } from '../utils/groceryProcessor';

const ShoppingPage = () => {
  const { allPlan, fetchPlans, selectedPlanId, selectPlan, currentPlan, fetchPlan } = usePlanStore();
  const [shoppingList, setShoppingList] = useState([]);
  const [generatedList, setGeneratedList] = useState(null); // Editable generated list
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, item: null, index: null });
  const { t } = useTranslation();
  const { showSuccess, showError, showWarning } = useSnackbar();

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    if (selectedPlanId) {
      fetchShoppingList(selectedPlanId);
      fetchPlan(selectedPlanId); // Fetch full plan details for generation
    }
  }, [selectedPlanId]);

  const fetchShoppingList = async (planId) => {
    try {
      setLoading(true);
      const data = await apiClient.get(`/api/v1/shopping-list?plan_id=${planId}`);
      setShoppingList(data);
    } catch (err) {
      showError('Failed to fetch shopping list', err);
    } finally {
      setLoading(false);
    }
  };

  const generateGroceryList = async () => {
    if (!currentPlan) {
      showWarning(t('shopping.selectPlanFirst', 'Please select a plan first'));
      return;
    }

    try {
      setGenerating(true);
      
      // Process plan data to extract ingredients
      const categorizedData = processGroceryData(currentPlan);
      const flatList = flattenGroceryData(categorizedData);
      
      if (flatList.length === 0) {
        showWarning(t('shopping.noIngredientsFound', 'No ingredients found in this plan'));
        return;
      }
      
      // Set generated list for editing
      setGeneratedList(flatList);
      
    } catch (err) {
      showError(t('shopping.generateError', 'Failed to generate grocery list'));
    } finally {
      setGenerating(false);
    }
  };

  const saveGroceryList = async () => {
    if (!generatedList || generatedList.length === 0) {
      return;
    }

    try {
      setLoading(true);
      
      // Save each item to backend
      const promises = generatedList.map(item => 
        apiClient.post('/api/v1/shopping-list', {
          plan_id: selectedPlanId,
          ...item
        })
      );
      
      await Promise.all(promises);
      
      // Refresh the shopping list
      await fetchShoppingList(selectedPlanId);
      
      // Clear generated list
      setGeneratedList(null);
      
      showSuccess(t('shopping.savedSuccessfully', 'Shopping list saved successfully!'));
      
    } catch (err) {
      console.error('Failed to save shopping list', err);
      showError(t('shopping.saveError', 'Failed to save shopping list'));
    } finally {
      setLoading(false);
    }
  };

  const handleEditItem = (item, index) => {
    setEditDialog({ open: true, item: { ...item }, index });
  };

  const handleUpdateItem = () => {
    const { item, index } = editDialog;
    const updatedList = [...generatedList];
    updatedList[index] = item;
    setGeneratedList(updatedList);
    setEditDialog({ open: false, item: null, index: null });
  };

  const handleDeleteItem = (index) => {
    const updatedList = generatedList.filter((_, i) => i !== index);
    setGeneratedList(updatedList);
  };

  const handleAddItem = () => {
    setEditDialog({
      open: true,
      item: {
        ingredient_name: '',
        total_amount: null,
        measure_unit: '',
        category: 'Other',
        quantity: '',
        is_purchased: false,
        notes: ''
      },
      index: -1 // -1 indicates new item
    });
  };

  const handleSaveDialog = () => {
    const { item, index } = editDialog;
    
    if (!item.ingredient_name) {
      showWarning(t('shopping.enterItemName', 'Please enter item name'));
      return;
    }
    
    if (index === -1) {
      // Add new item
      setGeneratedList([...generatedList, item]);
    } else {
      // Update existing item
      handleUpdateItem();
      return;
    }
    
    setEditDialog({ open: false, item: null, index: null });
  };

  // ==================== SAVED LIST FUNCTIONS ====================
  
  /**
   * Delete a saved shopping list item from database
   */
  const handleDeleteSavedItem = async (itemId) => {

    try {
      setLoading(true);
      await apiClient.delete(`/api/v1/shopping-list/${itemId}`);
      
      // Remove from local state
      setShoppingList(prev => prev.filter(item => item.id !== itemId));
      
      showSuccess(t('shopping.deleteSuccess', 'Item deleted successfully'));
    } catch (err) {
      console.error('Failed to delete item', err);
      showError(t('shopping.deleteError', 'Failed to delete item'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update a saved shopping list item in database
   */
  const handleUpdateSavedItem = async (itemId, updatedData) => {
    try {
      setLoading(true);
      const updated = await apiClient.put(`/api/v1/shopping-list/${itemId}`, updatedData);
      
      // Update local state
      setShoppingList(prev => 
        prev.map(item => item.id === itemId ? { ...item, ...updated } : item)
      );
      
      showSuccess(t('shopping.updateSuccess', 'Item updated successfully'));
    } catch (err) {
      console.error('Failed to update item', err);
      showError(t('shopping.updateError', 'Failed to update item'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Toggle purchased status of a saved item
   */
  const handleTogglePurchased = async (itemId, isPurchased) => {
    try {
      await apiClient.put(`/api/v1/shopping-list/${itemId}`, {
        is_purchased: isPurchased
      });
      
      // Update local state
      setShoppingList(prev =>
        prev.map(item =>
          item.id === itemId ? { ...item, is_purchased: isPurchased } : item
        )
      );
    } catch (err) {
      console.error('Failed to toggle purchased status', err);
      showError(t('shopping.toggleError', 'Failed to update item status'));
    }
  };

  /**
   * Edit a saved shopping list item
   */
  const handleEditSavedItem = (item, index) => {
    setEditDialog({
      open: true,
      item: { ...item },
      index: index,
      isSavedItem: true // Flag to indicate this is a saved item
    });
  };

  /**
   * Add a new item directly to saved shopping list
   */
  const handleAddSavedItem = () => {
    if (!selectedPlanId) {
      showWarning(t('shopping.selectPlanFirst', 'Please select a plan first'));
      return;
    }
    
    setEditDialog({
      open: true,
      item: {
        ingredient_name: '',
        total_amount: null,
        measure_unit: '',
        category: 'Other',
        quantity: '',
        is_purchased: false,
        notes: ''
      },
      index: -1,
      isSavedItem: true,
      isNewSavedItem: true // Flag for new saved item
    });
  };

  /**
   * Save changes to a saved item or create new
   */
  const handleSaveSavedItemDialog = async () => {
    const { item, isSavedItem, isNewSavedItem } = editDialog;
    
    if (!item.ingredient_name) {
      showWarning(t('shopping.enterItemName', 'Please enter item name'));
      return;
    }

    if (isNewSavedItem) {
      // Create new saved item
      try {
        setLoading(true);
        await apiClient.post('/api/v1/shopping-list', {
          plan_id: selectedPlanId,
          ingredient_name: item.ingredient_name,
          total_amount: item.total_amount,
          measure_unit: item.measure_unit,
          category: item.category,
          notes: item.notes,
          is_purchased: false
        });
        
        // Refresh the shopping list
        await fetchShoppingList(selectedPlanId);
        
        showSuccess(t('shopping.addSuccess', 'Item added successfully'));
      } catch (err) {
        console.error('Failed to add item', err);
        showError(t('shopping.addError', 'Failed to add item'));
      } finally {
        setLoading(false);
      }
    } else if (isSavedItem && item.id) {
      // Update existing saved item
      await handleUpdateSavedItem(item.id, {
        ingredient_name: item.ingredient_name,
        total_amount: item.total_amount,
        measure_unit: item.measure_unit,
        category: item.category,
        notes: item.notes
      });
    }
    
    setEditDialog({ open: false, item: null, index: null, isSavedItem: false, isNewSavedItem: false });
  };

  return (
    <Container sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('shopping.title', 'Shopping')}
      </Typography>

      {/* Plan Selection and Actions */}
      <Box 
        display="flex" 
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={2} 
        alignItems="center"
        justifyContent="center"
        mb={3}
      >
        <Select
          value={selectedPlanId || ''}
          onChange={(e) => selectPlan(e.target.value)}
          displayEmpty
          sx={{ minWidth: { xs: '100%', sm: 240 } }}
        >
          <MenuItem value="">
            {t('plans.selectWeekRange', 'Select a plan')}
          </MenuItem>
          {allPlan && allPlan.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.start_date} - {p.end_date}
            </MenuItem>
          ))}
        </Select>
        
        <Button
          variant="contained"
          color="primary"
          onClick={generateGroceryList}
          disabled={!selectedPlanId || generating}
          startIcon={generating ? <CircularProgress size={20} /> : <AddIcon />}
          sx={{ 
            width: { xs: '100%', sm: 'auto' },
            whiteSpace: 'nowrap'
          }}
        >
          {t('shopping.generateList', 'Generate Grocery List')}
        </Button>
        
        {/* <Button
          variant="outlined"
          onClick={() => fetchShoppingList(selectedPlanId)}
          disabled={!selectedPlanId}
          fullWidth={{ xs: true, sm: false }}
        >
          {t('common.buttons.refresh', 'Refresh')}
        </Button> */}
      </Box>

      {/* Generated List (Editable) - Step 3: Edit before save */}
      {generatedList && (
        <Box mb={3}>
          <GroceryListContainer
            items={generatedList}
            title={t('shopping.generatedList', 'Generated Grocery List')}
            editable={true}
            showActions={true}
            onEditItem={handleEditItem}
            onDeleteItem={handleDeleteItem}
            onAddItem={handleAddItem}
            onSaveList={saveGroceryList}
            onCancel={() => setGeneratedList(null)}
            loading={loading}
            isGeneratedList={true}
          />
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Saved Shopping List */}
        <Grid item xs={12} md={8}>
          <GroceryListContainer
            items={shoppingList}
            title={t('shopping.savedList', 'Saved Shopping List')}
            editable={true}
            showActions={true}
            onEditItem={handleEditSavedItem}
            onDeleteItem={(index) => {
              const item = shoppingList[index];
              if (item && item.id) {
                handleDeleteSavedItem(item.id);
              }
            }}
            onAddItem={handleAddSavedItem}
            onTogglePurchased={handleTogglePurchased}
            loading={loading}
            isGeneratedList={false}
            emptyMessage={t('shopping.empty', 'No items. Generate and save a grocery list to get started.')}
          />
        </Grid>

        {/* Receipt Upload Section */}
        <Grid item xs={12} md={4}>
          <ReceiptUpload
            shoppingListId={selectedPlanId}
            onUploadSuccess={() => {
              console.log('Receipt uploaded successfully');
            }}
          />
        </Grid>
      </Grid>

      {/* Edit/Add Item Dialog */}
      <Dialog 
        open={editDialog.open} 
        onClose={() => setEditDialog({ open: false, item: null, index: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editDialog.index === -1 
            ? t('shopping.addItem', 'Add Item') 
            : t('shopping.editItem', 'Edit Item')}
        </DialogTitle>
        <DialogContent>
          <TextField
            label={t('shopping.itemName', 'Item Name')}
            fullWidth
            margin="normal"
            value={editDialog.item?.ingredient_name || ''}
            onChange={(e) => setEditDialog({
              ...editDialog,
              item: { ...editDialog.item, ingredient_name: e.target.value }
            })}
            autoFocus
          />
          <TextField
            label={t('shopping.amount', 'Amount')}
            type="number"
            fullWidth
            margin="normal"
            value={editDialog.item?.total_amount || ''}
            onChange={(e) => setEditDialog({
              ...editDialog,
              item: { ...editDialog.item, total_amount: parseFloat(e.target.value) || null }
            })}
          />
          <TextField
            label={t('shopping.unit', 'Unit')}
            fullWidth
            margin="normal"
            value={editDialog.item?.measure_unit || ''}
            onChange={(e) => setEditDialog({
              ...editDialog,
              item: { ...editDialog.item, measure_unit: e.target.value }
            })}
            placeholder="e.g., kg, lbs, pieces"
          />
          <Select
            fullWidth
            margin="dense"
            value={editDialog.item?.category || 'Other'}
            onChange={(e) => setEditDialog({
              ...editDialog,
              item: { ...editDialog.item, category: e.target.value }
            })}
            sx={{ mt: 2 }}
          >
            <MenuItem value="Produce">Produce</MenuItem>
            <MenuItem value="Meat">Meat</MenuItem>
            <MenuItem value="Dairy">Dairy</MenuItem>
            <MenuItem value="Pantry">Pantry</MenuItem>
            <MenuItem value="Frozen">Frozen</MenuItem>
            <MenuItem value="Bakery">Bakery</MenuItem>
            <MenuItem value="Beverages">Beverages</MenuItem>
            <MenuItem value="Household">Household</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </Select>
          <TextField
            label={t('shopping.notes', 'Notes')}
            fullWidth
            margin="normal"
            multiline
            rows={2}
            value={editDialog.item?.notes || ''}
            onChange={(e) => setEditDialog({
              ...editDialog,
              item: { ...editDialog.item, notes: e.target.value }
            })}
            placeholder="Optional notes"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, item: null, index: null, isSavedItem: false })}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button 
            onClick={editDialog.isSavedItem ? handleSaveSavedItemDialog : handleSaveDialog} 
            variant="contained"
          >
            {t('common.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ShoppingPage;
